import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { createLLMKey, deleteLLMKey, listLLMKeys } from './api';

export function LLMKeysPage() {
  const { org } = useCurrentOrg();
  const qc = useQueryClient();
  const orgId = org?.id ?? '';

  const keys = useQuery({
    queryKey: ['llm-keys', orgId],
    queryFn: () => listLLMKeys(orgId),
    enabled: !!orgId,
  });

  const [name, setName] = useState('');
  const [plaintext, setPlaintext] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createLLMKey(orgId, { name, provider: 'anthropic', plaintext }),
    onSuccess: () => {
      setName('');
      setPlaintext('');
      qc.invalidateQueries({ queryKey: ['llm-keys', orgId] });
    },
    onError: (e: unknown) => setError(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteLLMKey(orgId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['llm-keys', orgId] }),
  });

  function handleCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name || !plaintext.startsWith('sk-ant-')) {
      setError('name is required and Anthropic keys start with sk-ant-');
      return;
    }
    create.mutate();
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tightish text-ink-primary">LLM keys</h1>
        <p className="mt-1 text-sm text-ink-secondary">
          BYO Anthropic API keys. Stored encrypted with envelope encryption (KMS-wrapped DEK +
          AES-GCM). Plaintext is decrypted only inside the runner at the start of each run, never
          shown again.
        </p>
      </div>

      <Card>
        <CardHeader title="Add a key" />
        <CardBody>
          <form onSubmit={handleCreate} className="grid gap-4 sm:grid-cols-3">
            <Input
              label="Name"
              placeholder="prod"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <div className="sm:col-span-2">
              <Input
                label="Anthropic key (sk-ant-…)"
                type="password"
                placeholder="sk-ant-api03-..."
                value={plaintext}
                onChange={(e) => setPlaintext(e.target.value)}
                required
              />
            </div>
            {error && (
              <div className="sm:col-span-3 rounded-md border border-danger/20 bg-danger/5 px-3 py-2 text-sm text-danger">
                {error}
              </div>
            )}
            <div className="sm:col-span-3 flex justify-end">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Saving…' : 'Save key'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Your keys" />
        <CardBody className="p-0">
          {keys.data && keys.data.length === 0 && (
            <div className="p-6 text-sm text-ink-tertiary">No keys yet.</div>
          )}
          {keys.data && keys.data.length > 0 && (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-line-soft text-xs uppercase tracking-eyebrow text-ink-tertiary">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Provider</th>
                  <th className="px-4 py-2 font-medium">Prefix</th>
                  <th className="px-4 py-2 font-medium">Last used</th>
                  <th className="px-4 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {keys.data.map((k) => (
                  <tr key={k.id} className="border-t border-line-soft hover:bg-surface-soft/40">
                    <td className="px-4 py-2 font-medium text-ink-primary">{k.name}</td>
                    <td className="px-4 py-2 text-xs text-ink-secondary">{k.provider}</td>
                    <td className="px-4 py-2 font-mono text-xs text-ink-secondary">{k.key_prefix}…</td>
                    <td className="px-4 py-2 text-xs text-ink-secondary">
                      {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          if (confirm('Delete this key? Agents using it will fail.')) {
                            remove.mutate(k.id);
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { data?: { error?: { message?: string; code?: string } } } }).response;
    if (resp?.data?.error) return resp.data.error.message ?? resp.data.error.code ?? 'unknown error';
  }
  return e instanceof Error ? e.message : 'unknown error';
}
